import React, { useState, useMemo, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useVirtualizer } from "@tanstack/react-virtual";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Search, Plus, Trash2, Check, X, Filter, PlusCircle, Star, StopCircle, Pencil } from "lucide-react";
import StatusBadge from "../components/StatusBadge";
import { useModules } from "../contexts/ModulesContext";
import { toast } from "sonner";

function useDebounce(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

const PAGE_SIZE = 200;

export default function Users() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const debounced = useDebounce(search, 300);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [creating, setCreating] = useState(false);
  const scrollRef = useRef(null);

  // Auto-open the user drawer when navigated with ?focus=<userId>
  useEffect(() => {
    const focus = searchParams.get("focus");
    if (focus) {
      setSelectedId(focus);
      // Clear the param so refresh doesn't re-trigger
      const next = new URLSearchParams(searchParams);
      next.delete("focus");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const { data, isLoading } = useQuery({
    queryKey: ["users", debounced, statusFilter],
    queryFn: async () => {
      const { data } = await api.get("/users", { params: { page: 1, limit: 5000, q: debounced || undefined } });
      let rows = data.data;
      if (statusFilter !== "all") rows = rows.filter((r) => r.account_status === statusFilter);
      return rows;
    },
  });

  const rows = data || [];

  const virt = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 48,
    overscan: 10,
  });

  const toggleAll = () => {
    if (selectedIds.size === rows.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(rows.map((r) => r.id)));
  };
  const toggleOne = (id) => {
    const s = new Set(selectedIds);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelectedIds(s);
  };

  const bulkStatus = async (status) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    await api.post("/users/bulk-status", { ids, account_status: status });
    setSelectedIds(new Set());
    qc.invalidateQueries({ queryKey: ["users"] });
    toast.success(`${ids.length} utilisateur(s) mis à jour`);
  };

  return (
    <div className="p-8 max-w-[1600px] mx-auto" data-testid="users-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-warm-dark">{t("users")}</h1>
          <p className="text-sm text-warm-dark/60 mt-1">{rows.length.toLocaleString()} {rows.length > 1 ? "résultats" : "résultat"}</p>
        </div>
        <Button data-testid="btn-new-user" onClick={() => setCreating(true)} className="bg-terracotta hover:bg-terracotta-dark">
          <Plus size={16} className="mr-1.5" />{t("new_user")}
        </Button>
      </div>

      <Card className="border-sand shadow-soft overflow-hidden">
        <div className="px-4 py-3 border-b border-sand flex items-center gap-3 bg-cream/40">
          <div className="relative flex-1 max-w-md">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-warm-dark/50" />
            <Input
              placeholder={t("search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="users-search-input"
              className="pl-9 h-9"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-warm-dark/50" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-9 w-40" data-testid="users-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("all")}</SelectItem>
                <SelectItem value="Active">{t("active")}</SelectItem>
                <SelectItem value="Inactive">{t("inactive")}</SelectItem>
                <SelectItem value="Suspended">{t("suspended")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-2 ml-auto text-sm">
              <span className="text-warm-dark/70">{selectedIds.size} {t("rows_selected")}</span>
              <Button size="sm" variant="outline" onClick={() => bulkStatus("Active")} data-testid="btn-bulk-activate">
                <Check size={14} className="mr-1.5" />{t("activate")}
              </Button>
              <Button size="sm" variant="outline" onClick={() => bulkStatus("Inactive")} data-testid="btn-bulk-deactivate">
                <X size={14} className="mr-1.5" />{t("deactivate")}
              </Button>
            </div>
          )}
        </div>

        {/* Table header */}
        <div className="grid grid-cols-[40px_1.2fr_1.5fr_1.8fr_120px_1fr] gap-4 px-4 py-2.5 bg-cream border-b border-sand text-xs font-semibold uppercase tracking-wider text-warm-dark/60 sticky top-0">
          <div>
            <Checkbox
              checked={rows.length > 0 && selectedIds.size === rows.length}
              onCheckedChange={toggleAll}
              data-testid="users-select-all"
            />
          </div>
          <div>Login</div>
          <div>{t("display_name")}</div>
          <div>{t("mail")}</div>
          <div>{t("account_status")}</div>
          <div>{t("employee_id")}</div>
        </div>

        {/* Virtualized rows */}
        <div ref={scrollRef} className="h-[calc(100vh-330px)] overflow-auto" data-testid="users-virtual-list">
          {isLoading ? (
            <div className="p-10 text-center text-warm-dark/50 text-sm">{t("loading")}</div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-warm-dark/50 text-sm">{t("no_results")}</div>
          ) : (
            <div style={{ height: virt.getTotalSize(), position: "relative" }}>
              {virt.getVirtualItems().map((vi) => {
                const u = rows[vi.index];
                return (
                  <div
                    key={u.id}
                    className="virt-row grid grid-cols-[40px_1.2fr_1.5fr_1.8fr_120px_1fr] gap-4 px-4 items-center text-sm cursor-pointer"
                    style={{ position: "absolute", top: 0, left: 0, right: 0, transform: `translateY(${vi.start}px)`, height: vi.size }}
                    onClick={() => setSelectedId(u.id)}
                    data-testid={`user-row-${u.id}`}
                  >
                    <div onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={selectedIds.has(u.id)} onCheckedChange={() => toggleOne(u.id)} />
                    </div>
                    <div className="font-mono text-xs text-warm-dark/80 truncate">{u.login}</div>
                    <div className="text-warm-dark font-medium truncate">{u.display_name}</div>
                    <InlineMailCell user={u} />
                    <div><StatusBadge kind="account_status" value={u.account_status} /></div>
                    <div className="text-warm-dark/60 font-mono text-xs truncate">{u.employee_id}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Card>

      <UserDetailDrawer userId={selectedId} onClose={() => setSelectedId(null)} />
      <UserCreateDialog open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}

function InlineMailCell({ user }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(user.mail || "");
  const inputRef = useRef(null);

  useEffect(() => { setValue(user.mail || ""); }, [user.mail]);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const commit = async () => {
    const trimmed = value.trim();
    if (trimmed === (user.mail || "")) { setEditing(false); return; }
    if (trimmed && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error("Email invalide");
      setValue(user.mail || "");
      setEditing(false);
      return;
    }
    try {
      await api.put(`/users/${user.id}`, {
        ...user,
        mail: trimmed || null,
        start_date: user.start_date || null,
        end_date: user.end_date || null,
      });
      toast.success("Email mis à jour");
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["user", user.id] });
    } catch {
      toast.error("Erreur");
      setValue(user.mail || "");
    } finally {
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <div onClick={(e) => e.stopPropagation()} data-testid={`inline-mail-${user.id}`}>
        <input
          ref={inputRef}
          type="email"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); }
            else if (e.key === "Escape") { setValue(user.mail || ""); setEditing(false); }
          }}
          className="w-full h-7 px-2 text-sm rounded-md border border-terracotta bg-white outline-none focus:ring-2 focus:ring-terracotta/30"
          data-testid={`inline-mail-input-${user.id}`}
        />
      </div>
    );
  }
  return (
    <div
      className="text-warm-dark/70 truncate cursor-text hover:bg-cream/60 rounded px-1 -mx-1 group flex items-center gap-1.5"
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      title="Cliquer pour modifier"
      data-testid={`inline-mail-display-${user.id}`}
    >
      <span className="truncate">{user.mail || "—"}</span>
      <Pencil size={10} className="opacity-0 group-hover:opacity-60 shrink-0 text-warm-dark/60" />
    </div>
  );
}

function UserDetailDrawer({ userId, onClose }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { isEnabled } = useModules();
  const multiRecord = isEnabled("multi_record");
  const [addRecOpen, setAddRecOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editRec, setEditRec] = useState(null);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const { data: user } = useQuery({
    queryKey: ["user", userId],
    enabled: !!userId,
    queryFn: async () => (await api.get(`/users/${userId}`)).data.data,
  });
  const { data: recs } = useQuery({
    queryKey: ["user-records-of", userId],
    enabled: !!userId,
    queryFn: async () => (await api.get("/user-records", { params: { limit: 50, q: "" } })).data.data.filter((r) => r.user_id === userId),
  });
  const { data: profile } = useQuery({
    queryKey: ["profile", userId],
    enabled: !!userId,
    queryFn: async () => {
      try { return (await api.get(`/profiles/${userId}`)).data.data; } catch { return null; }
    },
  });

  const del = async () => {
    if (!window.confirm("Supprimer cet utilisateur ?")) return;
    await api.delete(`/users/${userId}`);
    toast.success("Utilisateur supprimé");
    qc.invalidateQueries({ queryKey: ["users"] });
    onClose();
  };

  return (
    <Sheet open={!!userId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-[580px] sm:max-w-[580px] overflow-y-auto" data-testid="user-detail-drawer">
        <SheetHeader>
          <SheetTitle className="font-heading">
            {user?.display_name || t("loading")}
          </SheetTitle>
        </SheetHeader>
        {user && (
          <div className="mt-4">
            <div className="flex items-center gap-3 mb-4">
              <StatusBadge kind="account_status" value={user.account_status} />
              <span className="font-mono text-xs text-warm-dark/60">{user.employee_id}</span>
              <Button
                size="sm"
                variant="outline"
                className="ml-auto h-7 text-xs"
                onClick={() => setEditOpen(true)}
                data-testid="btn-edit-user"
              >
                <Pencil size={12} className="mr-1.5" />
                Modifier
              </Button>
            </div>
            <Tabs defaultValue="identity">
              <TabsList className="w-full grid grid-cols-3">
                <TabsTrigger value="identity" data-testid="tab-identity">{t("identity")}</TabsTrigger>
                <TabsTrigger value="record" data-testid="tab-record">{t("user_record")}</TabsTrigger>
                <TabsTrigger value="profile" data-testid="tab-profile">{t("profile")}</TabsTrigger>
              </TabsList>
              <TabsContent value="identity" className="space-y-3 mt-4">
                <Field label={t("login_placeholder")} value={user.login} mono />
                <Field label={t("first_name")} value={user.first_name} />
                <Field label={t("last_name")} value={user.last_name} />
                <Field label={t("mail")} value={user.mail} />
                <Field label={t("start_date")} value={user.start_date} />
                <Field label={t("end_date")} value={user.end_date} />
              </TabsContent>
              <TabsContent value="record" className="mt-4 space-y-3">
                {multiRecord && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full"
                    onClick={() => setAddRecOpen(true)}
                    data-testid="btn-add-record"
                  >
                    <PlusCircle size={14} className="mr-1.5" />
                    Ajouter une fiche RH
                  </Button>
                )}
                {(recs || []).map((r) => {
                  const setAsMain = async () => {
                    try {
                      await api.post(`/user-records/${r.id}/set-main`);
                      qc.invalidateQueries({ queryKey: ["user-records-of", userId] });
                      qc.invalidateQueries({ queryKey: ["user-records"] });
                      toast.success("Fiche principale mise à jour");
                    } catch { toast.error("Erreur"); }
                  };
                  const terminate = async () => {
                    try {
                      await api.patch(`/user-records/${r.id}`, { end_date: new Date().toISOString().slice(0, 10) });
                      qc.invalidateQueries({ queryKey: ["user-records-of", userId] });
                      qc.invalidateQueries({ queryKey: ["user-records"] });
                      toast.success("Fiche terminée");
                    } catch { toast.error("Erreur"); }
                  };
                  return (
                    <div key={r.id} className={`p-3 rounded-md border text-sm space-y-1.5 ${r.is_main ? "border-amber-200 bg-amber-50/30" : "border-sand"}`} data-testid={`record-card-${r.id}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-warm-dark flex items-center gap-1.5">
                          {r.is_main && <Star size={13} className="text-amber-500 fill-amber-400" data-testid={`drawer-main-${r.id}`} />}
                          {r.title || "—"}
                        </span>
                        <div className="flex items-center gap-1.5">
                          <StatusBadge kind="contract_type" value={r.contract_type} />
                          <button onClick={() => setEditRec(r)} className="text-warm-dark/50 hover:text-warm-dark" title="Modifier la fiche" data-testid={`drawer-edit-${r.id}`}>
                            <Pencil size={12} />
                          </button>
                          {multiRecord && !r.is_main && (
                            <button onClick={setAsMain} className="text-warm-dark/50 hover:text-amber-600" title="Définir comme principale" data-testid={`drawer-setmain-${r.id}`}>
                              <Star size={12} />
                            </button>
                          )}
                          {!r.end_date && (
                            <button onClick={terminate} className="text-warm-dark/50 hover:text-red-600" title="Terminer la fiche" data-testid={`drawer-end-${r.id}`}>
                              <StopCircle size={12} />
                            </button>
                          )}
                          {multiRecord && (
                            <button
                              onClick={async () => { await api.delete(`/user-records/${r.id}`); qc.invalidateQueries({ queryKey: ["user-records-of", userId] }); toast.success("Fiche supprimée"); }}
                              className="text-warm-dark/50 hover:text-red-600"
                              data-testid={`del-record-${r.id}`}
                            >
                              <Trash2 size={12} />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <StatusBadge kind="presence_state" value={r.presence_state} />
                        <StatusBadge kind="time_type" value={r.time_type} />
                        <span className="mono text-xs text-warm-dark/60">{r.working_rate}%</span>
                      </div>
                      <div className="text-xs text-warm-dark/60 mono">
                        {r.start_date || "?"} → {r.end_date || "—"}
                      </div>
                    </div>
                  );
                })}
                {(!recs || recs.length === 0) && <div className="text-sm text-warm-dark/50">{t("no_data")}</div>}
                {!multiRecord && recs && recs.length > 1 && (
                  <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                    Cet utilisateur a {recs.length} fiches mais le mode MultiRecord est désactivé. Activez-le dans Paramètres pour les gérer toutes.
                  </div>
                )}
              </TabsContent>
              <TabsContent value="profile" className="mt-4 space-y-3">
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full"
                  onClick={() => setEditProfileOpen(true)}
                  data-testid="btn-edit-profile"
                >
                  <Pencil size={12} className="mr-1.5" />
                  {profile ? "Modifier le profil étendu" : "Créer le profil étendu"}
                </Button>
                {profile ? (
                  <>
                    <Field label="Date de naissance" value={profile.birth_date} />
                    <Field label="Nationalité" value={profile.nationality} />
                    <Field label="N° badge" value={profile.badge_number} mono />
                    <Field label="Tél. interne" value={profile.phone_internal} mono />
                    <Field label="Email personnel" value={profile.personal_email} />
                  </>
                ) : <div className="text-sm text-warm-dark/50">{t("no_data")}</div>}
              </TabsContent>
            </Tabs>

            <div className="mt-6 pt-4 border-t border-sand flex justify-end">
              <Button variant="destructive" size="sm" onClick={del} data-testid="btn-delete-user">
                <Trash2 size={14} className="mr-1.5" />{t("delete")}
              </Button>
            </div>
          </div>
        )}
        <AddRecordDialog open={addRecOpen} userId={userId} onClose={() => setAddRecOpen(false)} />
        <UserEditDialog open={editOpen} user={user} onClose={() => setEditOpen(false)} />
        <RecordEditDialog open={!!editRec} record={editRec} onClose={() => setEditRec(null)} />
        <ProfileEditDialog open={editProfileOpen} userId={userId} profile={profile} onClose={() => setEditProfileOpen(false)} />
      </SheetContent>
    </Sheet>
  );
}

function UserEditDialog({ open, user, onClose }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (user && open) {
      setForm({
        login: user.login || "",
        employee_id: user.employee_id || "",
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        display_name: user.display_name || "",
        mail: user.mail || "",
        account_status: user.account_status || "Active",
        start_date: user.start_date || "",
        end_date: user.end_date || "",
      });
    }
  }, [user, open]);

  if (!form || !user) return null;

  const upd = (k, v) => setForm({ ...form, [k]: v });
  const submit = async () => {
    try {
      const payload = { ...form };
      // keep display_name coherent if first/last changed and user hasn't manually set it
      if (!payload.display_name || payload.display_name === `${user.first_name} ${user.last_name}`) {
        payload.display_name = `${payload.first_name} ${payload.last_name}`.trim();
      }
      // null-out empty date strings
      if (!payload.start_date) payload.start_date = null;
      if (!payload.end_date) payload.end_date = null;
      await api.put(`/users/${user.id}`, payload);
      toast.success("Utilisateur mis à jour");
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["user", user.id] });
      qc.invalidateQueries({ queryKey: ["users-lookup"] });
      onClose();
    } catch (e) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="user-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Modifier l'utilisateur</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-medium text-warm-dark/80 mb-1.5 block">Login</Label>
            <Input value={form.login} onChange={(e) => upd("login", e.target.value)} className="mono" data-testid="edit-login" />
          </div>
          <div>
            <Label className="text-xs font-medium text-warm-dark/80 mb-1.5 block">Matricule</Label>
            <Input value={form.employee_id} onChange={(e) => upd("employee_id", e.target.value)} className="mono" data-testid="edit-emp" />
          </div>
          <div>
            <Label className="text-xs font-medium text-warm-dark/80 mb-1.5 block">Prénom</Label>
            <Input value={form.first_name} onChange={(e) => upd("first_name", e.target.value)} data-testid="edit-first" />
          </div>
          <div>
            <Label className="text-xs font-medium text-warm-dark/80 mb-1.5 block">Nom</Label>
            <Input value={form.last_name} onChange={(e) => upd("last_name", e.target.value)} data-testid="edit-last" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs font-medium text-warm-dark/80 mb-1.5 block">Nom affiché</Label>
            <Input value={form.display_name} onChange={(e) => upd("display_name", e.target.value)} data-testid="edit-display" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs font-medium text-warm-dark/80 mb-1.5 block">Email</Label>
            <Input type="email" value={form.mail} onChange={(e) => upd("mail", e.target.value)} data-testid="edit-mail" />
          </div>
          <div>
            <Label className="text-xs font-medium text-warm-dark/80 mb-1.5 block">Statut</Label>
            <Select value={form.account_status} onValueChange={(v) => upd("account_status", v)}>
              <SelectTrigger data-testid="edit-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Active">Actif</SelectItem>
                <SelectItem value="Inactive">Inactif</SelectItem>
                <SelectItem value="Suspended">Suspendu</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div></div>
          <div>
            <Label className="text-xs font-medium text-warm-dark/80 mb-1.5 block">Date de début</Label>
            <Input type="date" value={form.start_date || ""} onChange={(e) => upd("start_date", e.target.value)} data-testid="edit-start" />
          </div>
          <div>
            <Label className="text-xs font-medium text-warm-dark/80 mb-1.5 block">Date de fin</Label>
            <Input type="date" value={form.end_date || ""} onChange={(e) => upd("end_date", e.target.value)} data-testid="edit-end" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} className="bg-terracotta hover:bg-terracotta-dark" data-testid="edit-submit">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddRecordDialog({ open, userId, onClose }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    title: "", contract_type: "CDI", presence_state: "Present", time_type: "FullTime",
    working_rate: 100, start_date: new Date().toISOString().slice(0, 10),
  });
  const submit = async () => {
    try {
      await api.post("/user-records", { ...form, user_id: userId, working_rate: Number(form.working_rate) });
      toast.success("Fiche RH créée");
      qc.invalidateQueries({ queryKey: ["user-records-of", userId] });
      qc.invalidateQueries({ queryKey: ["user-records"] });
      onClose();
      setForm({ title: "", contract_type: "CDI", presence_state: "Present", time_type: "FullTime", working_rate: 100, start_date: new Date().toISOString().slice(0, 10) });
    } catch {
      toast.error("Erreur");
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="add-record-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Nouvelle fiche RH</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-xs">Intitulé</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="nr-title" />
          </div>
          <div>
            <Label className="text-xs">Contrat</Label>
            <select className="w-full h-9 px-3 border border-sand rounded-md text-sm" value={form.contract_type} onChange={(e) => setForm({ ...form, contract_type: e.target.value })} data-testid="nr-contract">
              {["CDI", "CDD", "Stage", "Alternance", "Freelance"].map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Présence</Label>
            <select className="w-full h-9 px-3 border border-sand rounded-md text-sm" value={form.presence_state} onChange={(e) => setForm({ ...form, presence_state: e.target.value })} data-testid="nr-presence">
              {["Present", "Remote", "Leave", "Offboarding"].map((p) => <option key={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Temps</Label>
            <select className="w-full h-9 px-3 border border-sand rounded-md text-sm" value={form.time_type} onChange={(e) => setForm({ ...form, time_type: e.target.value })} data-testid="nr-time">
              <option>FullTime</option><option>PartTime</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Taux (%)</Label>
            <Input type="number" value={form.working_rate} onChange={(e) => setForm({ ...form, working_rate: e.target.value })} data-testid="nr-rate" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Date début</Label>
            <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="nr-start" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} className="bg-terracotta hover:bg-terracotta-dark" data-testid="nr-submit">Créer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-[11px] font-medium uppercase tracking-wider text-warm-dark/60">{label}</div>
      <div className={`text-sm text-warm-dark mt-0.5 ${mono ? "mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}

function UserCreateDialog({ open, onClose }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [form, setForm] = useState({
    login: "", first_name: "", last_name: "", mail: "", employee_id: "", account_status: "Active",
  });
  const upd = (k, v) => setForm({ ...form, [k]: v });

  const submit = async () => {
    try {
      await api.post("/users", { ...form, display_name: `${form.first_name} ${form.last_name}` });
      toast.success("Utilisateur créé");
      qc.invalidateQueries({ queryKey: ["users"] });
      onClose();
      setForm({ login: "", first_name: "", last_name: "", mail: "", employee_id: "", account_status: "Active" });
    } catch (e) {
      toast.error("Erreur lors de la création");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="user-create-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{t("new_user")}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <FormField label={t("login_placeholder")} value={form.login} onChange={(v) => upd("login", v)} testId="nu-login" />
          <FormField label={t("employee_id")} value={form.employee_id} onChange={(v) => upd("employee_id", v)} testId="nu-emp" />
          <FormField label={t("first_name")} value={form.first_name} onChange={(v) => upd("first_name", v)} testId="nu-first" />
          <FormField label={t("last_name")} value={form.last_name} onChange={(v) => upd("last_name", v)} testId="nu-last" />
          <div className="col-span-2">
            <FormField label={t("mail")} value={form.mail} onChange={(v) => upd("mail", v)} testId="nu-mail" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs font-medium text-warm-dark/80 mb-1.5 block">{t("account_status")}</Label>
            <Select value={form.account_status} onValueChange={(v) => upd("account_status", v)}>
              <SelectTrigger data-testid="nu-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Active">{t("active")}</SelectItem>
                <SelectItem value="Inactive">{t("inactive")}</SelectItem>
                <SelectItem value="Suspended">{t("suspended")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
          <Button onClick={submit} data-testid="btn-submit-new-user" className="bg-terracotta hover:bg-terracotta-dark">{t("save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FormField({ label, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-xs font-medium text-warm-dark/80 mb-1.5 block">{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
    </div>
  );
}

function RecordEditDialog({ open, record, onClose }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(null);
  const { data: sites } = useQuery({
    queryKey: ["sites-lookup"],
    queryFn: async () => (await api.get("/sites", { params: { limit: 500 } })).data.data,
    enabled: open,
  });
  const { data: services } = useQuery({
    queryKey: ["services-lookup"],
    queryFn: async () => (await api.get("/services", { params: { limit: 500 } })).data.data,
    enabled: open,
  });

  useEffect(() => {
    if (record && open) {
      setForm({
        title: record.title || "",
        contract_type: record.contract_type || "CDI",
        presence_state: record.presence_state || "Present",
        time_type: record.time_type || "FullTime",
        working_rate: record.working_rate ?? 100,
        site_id: record.site_id || "",
        service_id: record.service_id || "",
        start_date: record.start_date || "",
        end_date: record.end_date || "",
      });
    }
  }, [record, open]);

  if (!form || !record) return null;

  const upd = (k, v) => setForm({ ...form, [k]: v });
  const submit = async () => {
    try {
      const payload = { ...form };
      payload.working_rate = Number(payload.working_rate);
      if (!payload.site_id) payload.site_id = null;
      if (!payload.service_id) payload.service_id = null;
      if (!payload.start_date) payload.start_date = null;
      if (!payload.end_date) payload.end_date = null;
      await api.patch(`/user-records/${record.id}`, payload);
      toast.success("Fiche mise à jour");
      qc.invalidateQueries({ queryKey: ["user-records-of", record.user_id] });
      qc.invalidateQueries({ queryKey: ["user-records"] });
      onClose();
    } catch {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="record-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Modifier la fiche RH</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-xs font-medium text-warm-dark/80 mb-1.5 block">Intitulé</Label>
            <Input value={form.title} onChange={(e) => upd("title", e.target.value)} data-testid="er-title" />
          </div>
          <div>
            <Label className="text-xs font-medium text-warm-dark/80 mb-1.5 block">Contrat</Label>
            <Select value={form.contract_type} onValueChange={(v) => upd("contract_type", v)}>
              <SelectTrigger data-testid="er-contract"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["CDI", "CDD", "Stage", "Alternance", "Freelance"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-medium text-warm-dark/80 mb-1.5 block">Présence</Label>
            <Select value={form.presence_state} onValueChange={(v) => upd("presence_state", v)}>
              <SelectTrigger data-testid="er-presence"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Present", "Remote", "Leave", "Offboarding"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-medium text-warm-dark/80 mb-1.5 block">Temps</Label>
            <Select value={form.time_type} onValueChange={(v) => upd("time_type", v)}>
              <SelectTrigger data-testid="er-time"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="FullTime">FullTime</SelectItem>
                <SelectItem value="PartTime">PartTime</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-medium text-warm-dark/80 mb-1.5 block">Taux (%)</Label>
            <Input type="number" value={form.working_rate} onChange={(e) => upd("working_rate", e.target.value)} data-testid="er-rate" />
          </div>
          <div>
            <Label className="text-xs font-medium text-warm-dark/80 mb-1.5 block">Site</Label>
            <Select value={form.site_id || "__none__"} onValueChange={(v) => upd("site_id", v === "__none__" ? "" : v)}>
              <SelectTrigger data-testid="er-site"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">—</SelectItem>
                {(sites || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-medium text-warm-dark/80 mb-1.5 block">Service</Label>
            <Select value={form.service_id || "__none__"} onValueChange={(v) => upd("service_id", v === "__none__" ? "" : v)}>
              <SelectTrigger data-testid="er-service"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">—</SelectItem>
                {(services || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-medium text-warm-dark/80 mb-1.5 block">Date de début</Label>
            <Input type="date" value={form.start_date || ""} onChange={(e) => upd("start_date", e.target.value)} data-testid="er-start" />
          </div>
          <div>
            <Label className="text-xs font-medium text-warm-dark/80 mb-1.5 block">Date de fin</Label>
            <Input type="date" value={form.end_date || ""} onChange={(e) => upd("end_date", e.target.value)} data-testid="er-end" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} className="bg-terracotta hover:bg-terracotta-dark" data-testid="er-submit">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProfileEditDialog({ open, userId, profile, onClose }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (open && userId) {
      setForm({
        birth_date: profile?.birth_date || "",
        nationality: profile?.nationality || "",
        badge_number: profile?.badge_number || "",
        phone_internal: profile?.phone_internal || "",
        personal_email: profile?.personal_email || "",
      });
    }
  }, [profile, open, userId]);

  if (!form || !userId) return null;

  const upd = (k, v) => setForm({ ...form, [k]: v });
  const submit = async () => {
    try {
      const payload = { user_id: userId, ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      // POST /profiles — backend upserts (id = user_id, deletes existing then inserts)
      await api.post("/profiles", payload);
      toast.success("Profil étendu mis à jour");
      qc.invalidateQueries({ queryKey: ["profile", userId] });
      onClose();
    } catch {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="profile-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Modifier le profil étendu</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-medium text-warm-dark/80 mb-1.5 block">Date de naissance</Label>
            <Input type="date" value={form.birth_date || ""} onChange={(e) => upd("birth_date", e.target.value)} data-testid="ep-birth" />
          </div>
          <div>
            <Label className="text-xs font-medium text-warm-dark/80 mb-1.5 block">Nationalité</Label>
            <Input value={form.nationality || ""} onChange={(e) => upd("nationality", e.target.value)} data-testid="ep-nat" />
          </div>
          <div>
            <Label className="text-xs font-medium text-warm-dark/80 mb-1.5 block">N° badge</Label>
            <Input value={form.badge_number || ""} onChange={(e) => upd("badge_number", e.target.value)} className="mono" data-testid="ep-badge" />
          </div>
          <div>
            <Label className="text-xs font-medium text-warm-dark/80 mb-1.5 block">Tél. interne</Label>
            <Input value={form.phone_internal || ""} onChange={(e) => upd("phone_internal", e.target.value)} className="mono" data-testid="ep-phone" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs font-medium text-warm-dark/80 mb-1.5 block">Email personnel</Label>
            <Input type="email" value={form.personal_email || ""} onChange={(e) => upd("personal_email", e.target.value)} data-testid="ep-email" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} className="bg-terracotta hover:bg-terracotta-dark" data-testid="ep-submit">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
